// Inspired by react-hot-toast library
// shadcn/ui のコード
import * as React from "react";

import { getCurrentUnixTimeStamp } from "@/lib/dayjs/get-current-unix-time-stamp";

import type { ToastActionElement, ToastProps } from "../../../../ui/toast/toast";

const TOAST_LIMIT = 3;
const TOAST_REMOVE_DELAY = 0;

export type ToasterToast = ToastProps & {
	id: string;
	title?: string;
	description?: string;
	action?: ToastActionElement;
};

const actionTypes = {
	ADD_TOAST: "ADD_TOAST",
	UPDATE_TOAST: "UPDATE_TOAST",
	DISMISS_TOAST: "DISMISS_TOAST",
	REMOVE_TOAST: "REMOVE_TOAST",
} as const;

function genId({ ...props }: Toast) {
	// Strict mode の影響で同じ通知が複数表示されないようにIDを生成している
	const now = getCurrentUnixTimeStamp();
	const { description = now, title = now, variant = now } = props;

	const id = `#variant:${variant}_#title:${title}_#description:${description}`;
	return id;
}

type ActionType = typeof actionTypes;

type Action =
	| {
			type: ActionType["ADD_TOAST"];
			toast: ToasterToast;
	  }
	| {
			type: ActionType["UPDATE_TOAST"];
			toast: Partial<ToasterToast>;
	  }
	| {
			type: ActionType["DISMISS_TOAST"];
			toastId?: ToasterToast["id"];
	  }
	| {
			type: ActionType["REMOVE_TOAST"];
			toastId?: ToasterToast["id"];
	  };

interface State {
	toasts: ToasterToast[];
}

const toastTimeouts = new Map<string, ReturnType<typeof setTimeout>>();

const addToRemoveQueue = (toastId: string) => {
	if (toastTimeouts.has(toastId)) {
		return;
	}

	const timeout = setTimeout(() => {
		toastTimeouts.delete(toastId);
		dispatch({
			type: "REMOVE_TOAST",
			toastId: toastId,
		});
	}, TOAST_REMOVE_DELAY);

	toastTimeouts.set(toastId, timeout);
};

const reducer = (state: State, action: Action): State => {
	switch (action.type) {
		// Toastを追加する
		case "ADD_TOAST":
			return {
				...state,
				toasts: [action.toast, ...state.toasts].slice(0, TOAST_LIMIT),
			};

		case "UPDATE_TOAST":
			return {
				...state,
				toasts: state.toasts.map((t) => (t.id === action.toast.id ? { ...t, ...action.toast } : t)),
			};

		case "DISMISS_TOAST": {
			const { toastId } = action;

			// ! Side effects ! - This could be extracted into a dismissToast() action,
			// but I'll keep it here for simplicity
			if (toastId) {
				addToRemoveQueue(toastId);
			} else {
				state.toasts.forEach((toast) => {
					addToRemoveQueue(toast.id);
				});
			}

			return {
				...state,
				toasts: state.toasts.map((t) =>
					t.id === toastId || toastId === undefined
						? {
								...t,
								open: false,
							}
						: t,
				),
			};
		}
		case "REMOVE_TOAST":
			if (action.toastId === undefined) {
				return {
					...state,
					toasts: [],
				};
			}
			return {
				...state,
				toasts: state.toasts.filter((t) => t.id !== action.toastId),
			};
	}
};

const listeners: Array<(state: State) => void> = [];

/*
 * ここでstateを管理している
 * ここで管理しているstateは、useToast()で返している
 */
let memoryState: State = { toasts: [] };

function dispatch(action: Action) {
	memoryState = reducer(memoryState, action);

	listeners.forEach((listener) => {
		listener(memoryState);
	});
}

type Toast = Omit<ToasterToast, "id">;

/**
 * Toastを表示するための関数
 *
 * @example
 * notifyUserEventInfo({ variant: "default", title: "サインアウトしました。" })
 * @example
 * notifyUserEventInfo({ variant: "error", title: "予期せぬエラーが発生しました。", description: "カスタマーサクセスにご連絡ください。" })
 * @example
 * notifyUserEventInfo({ variant: "gift", title: "認証メールを送信しました", description: "メールをご確認ください。", action: <ToastAction altText="OK!">OK!</ToastAction>, })
 *
 * @description
 * title: 句読点なし
 * description:
 *  正確な日本語として伝える
 *  仮に、説明として長くなる場合や複数の説明が必要な場合は、別途議論をする
 *
 */
function notifyUserEventInfo({ ...props }: Toast) {
	const id = genId(props);

	if (memoryState.toasts.some((t) => t.id === id || t.title === props.title)) {
		// id か title が同じ通知がすでに表示されている場合は、表示しない
		return;
	}

	const update = (props: ToasterToast) =>
		dispatch({
			type: "UPDATE_TOAST",
			toast: { ...props, id },
		});
	const dismiss = () => dispatch({ type: "DISMISS_TOAST", toastId: id });

	// 通知を配列に追加する（通知を表示する）
	setTimeout(() => {
		dispatch({
			type: "ADD_TOAST",
			toast: {
				...props,
				id,
				open: true,
				onOpenChange: (open) => {
					if (!open) dismiss();
				},
			},
		});
	}, 0);

	return {
		id: id,
		dismiss,
		update,
	};
}

function useToastStore() {
	const [state, setState] = React.useState<State>(memoryState);

	React.useEffect(() => {
		listeners.push(setState);

		return () => {
			const index = listeners.indexOf(setState);
			if (index > -1) {
				// listeners にすでにsetStateが登録されている場合は、削除する（常に１つにする）
				listeners.splice(index, 1);
			}
		};
	}, [state]);

	return {
		...state,
		notifyUserEventInfo,
		dismiss: (toastId?: string) => dispatch({ type: "DISMISS_TOAST", toastId }),
	};
}

export { notifyUserEventInfo, useToastStore };
